import React, { useState, Fragment } from "react"
import { isMerchantOpen } from "../../../helpers/IsoDateHelpers"
import * as SelectionFactoryHelper from "../../helpers/SelectionFactoryHelper"
import Svg from "../../../shared/_svgs/svg"
import ReactHtmlParser from "react-html-parser"
import MerchantPageOtherStores from "./other-stores/merchant-page-other-stores"
import useWindowWidth from "../../../hooks/useWindowWidth"
import useKeyboardNavigation from "../../../hooks/useKeyboardNavigation"
import { accessibilityEnterCheck } from "../../../helpers/accessibilityHelper"

const MerchantPage = ({
  lang,
  mondayOpening,
  tuesdayOpening,
  wednesdayOpening,
  thursdayOpening,
  fridayOpening,
  saturdayOpening,
  sundayOpening,
  mondayClosing,
  tuesdayClosing,
  wednesdayClosing,
  thursdayClosing,
  fridayClosing,
  saturdayClosing,
  sundayClosing,
  openingHoursOverwrite,
  openingHours,
  sameshops,
  openingHoursText,
  openNowLabel,
  supportClickAndCollect,
  supportHomeDelivery,
  supportOrderNow,
  orderNowLink,
  phone,
  emailAddress,
  shopLocation,
  alwaysOpen = false,
  backgroundImage,
  richText,
  clickAndCollectWebSiteAddress,
  linkTargetSettings,
  ribbonText,
  ribbonColor,
  merchantGateProximity,
  merchantGroup,
  merchantPageLabels,
  weekdays,
  heroColorMain,
  heroColorOpeningHours,
  chooseDescription,
  descriptionHeader,
  description,
  descriptionShort,
  customCategoryLabel,
  categoriesText,
  categories,
  pageTitle,
  webLogoUpload,
  webLogo,
  webSiteAddress,
  otherStores,
  similarStores,
  googleMapsLink,
}) => {
  const windowWidth = useWindowWidth()
  const isKeyboardNavigating = useKeyboardNavigation()

  const openingHoursArray = openingHours && [
    openingHours.MondayOpening,
    openingHours.TuesdayOpening,
    openingHours.WednesdayOpening,
    openingHours.ThursdayOpening,
    openingHours.FridayOpening,
    openingHours.SaturdayOpening,
    openingHours.SundayOpening,
  ]
  const closingHoursArray = openingHours && [
    openingHours.MondayClosing,
    openingHours.TuesdayClosing,
    openingHours.WednesdayClosing,
    openingHours.ThursdayClosing,
    openingHours.FridayClosing,
    openingHours.SaturdayClosing,
    openingHours.SundayClosing,
  ]
  const now = new Date()
  const dayNumberStartingMonday = now.getDay() === 0 ? 6 : now.getDay() - 1
  const [openingHoursOpen, setOpeningHoursOpen] = useState(false)

  const [isReadingMore, setIsReadingMore] = useState(false)

  const openingHoursForTheDay = openingHoursArray ? openingHoursArray[dayNumberStartingMonday] : null
  const closingHoursForTheDay = closingHoursArray ? closingHoursArray[dayNumberStartingMonday] : null

  const merchantIsOpen = openingHoursForTheDay && closingHoursForTheDay && isMerchantOpen(openingHoursForTheDay, closingHoursForTheDay, lang)

  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const selectedShopLocation = SelectionFactoryHelper.getFromSelectionFactory(shopLocation)

  function FormatOpeningHoursTable(fromTimeArray, toTimeArray) {
    let formattedRanges = []

    for (let i = 0; i < fromTimeArray.length; i++) {
      formattedRanges.push(fromTimeArray[i] + " - " + toTimeArray[i])
    }
    return formattedRanges
  }

  const renderOpeningHours = () => {
    let weeklyOpeningHours = FormatOpeningHoursTable(openingHoursArray, closingHoursArray)
    return weeklyOpeningHours.map((openingHour, index) => {
      const day = capitalizeFirstLetter(weekdays[index])
      if (dayNumberStartingMonday === index) {
        return (
          <div key={day} className="day">
            <div role="listitem" tabIndex="0" className="day-container">
              <strong>
                <p className="day-name">{day}</p>
                <p className="day-opening-hour">{openingHour}</p>
              </strong>
            </div>
          </div>
        )
      }
      return (
        <div key={day} role="listitem" tabIndex="0" className="day">
          <div className="day-container">
            <p className="day-name">{day}</p>
            <p className="day-opening-hour">{openingHour}</p>
          </div>
        </div>
      )
    })
  }

  const handleOpeningHours = (e) => {
    accessibilityEnterCheck(e, () => setOpeningHoursOpen(!openingHoursOpen))
  }
  const splashlabel = SelectionFactoryHelper.getFromSelectionFactory(ribbonText)
  const ribbonColorSelected = SelectionFactoryHelper.getFromSelectionFactory(ribbonColor)
  const storeIsRebuilding = ribbonColorSelected == "theme-mandarin" && !!splashlabel
  const mapsLinkTarget = SelectionFactoryHelper.getFromSelectionFactory(merchantPageLabels.googleMapsLinkTarget)

  return (
    <div className="merchant-page-container">
      <div className="merchant-page-content-container">
        <div className="merchant-page-hero w--wider">
          {backgroundImage ? (
            <div className={`merchant-page-image-container${storeIsRebuilding ? " deactivated" : ""}`}>
              {storeIsRebuilding ? <div className={`splash ${ribbonColorSelected}`}>{splashlabel}</div> : undefined}
              <img src={backgroundImage}></img>
            </div>
          ) : undefined}
          <div className="merchant-page-details-container">
            {windowWidth >= 768 ? (
              <div className="merchantlogo">
                <img src={webLogoUpload ?? webLogo} data-epi-edit="WebLogo" loading="eager" fetchPriority="high" />
              </div>
            ) : undefined}
            <h1>{pageTitle}</h1>
            <div className="merchant-info-card">
              {/** shop location */}
              <div className="infoline">
                <Svg iconName={"mim-pin"} />
                <p>
                  {selectedShopLocation}
                  {googleMapsLink ? (
                    <span>
                      &ensp;–&ensp;
                      <a className="maps-link" href={googleMapsLink} target={mapsLinkTarget}>
                        {merchantPageLabels.googleMapsLinkLabel}
                      </a>
                    </span>
                  ) : (
                    ""
                  )}
                </p>
              </div>
              {/** Phone number */}
              {phone ? (
                <div className="infoline">
                  {windowWidth < 768 ? (
                    <a href={`tel:` + phone} className="contact-link">
                      <Svg iconName={"mim-phone"} />
                      {phone}
                    </a>
                  ) : (
                    <p>
                      <Svg iconName={"mim-phone"} />
                      {phone}
                    </p>
                  )}
                </div>
              ) : undefined}
              {/** Email Address */}
              {emailAddress ? (
                <div className="infoline contact-link">
                  <a href={`mailto:` + emailAddress}>
                    <Svg iconName={"mim-mail"} />
                    {merchantPageLabels.emailCoverLabel}
                  </a>
                </div>
              ) : undefined}
              {supportClickAndCollect && clickAndCollectWebSiteAddress ? (
                <div className="infoline">
                  <a className="web-link" href={clickAndCollectWebSiteAddress} target={linkTargetSettings}>
                    <Svg iconName={"mim-bag"} />
                    {merchantPageLabels.clickAndCollectLabel}
                  </a>
                </div>
              ) : undefined}
              {supportHomeDelivery ? (
                <div className="infoline">
                  <p>
                    <Svg iconName={"homedelivery"} />
                    {merchantPageLabels.homeDeliveryLabel}
                  </p>
                </div>
              ) : null}
              {supportOrderNow ? (
                <div className="infoline">
                  <a className="web-link" href={orderNowLink} target="_blank" rel="noreferrer">
                    <Svg iconName={"ordernow"} />
                    {merchantPageLabels.orderNowLabel}
                  </a>
                </div>
              ) : null}
              {/** opening hours */}
              <div className={`opening-hours-table infoline${isKeyboardNavigating ? " focus-within" : ""}`}>
                <input
                  id="show-openinghours"
                  type="checkbox"
                  tabIndex={alwaysOpen ? -1 : 0}
                  checked={openingHoursOpen}
                  onChange={(e) => {
                    handleOpeningHours(e)
                  }}
                />
                {openingHoursOverwrite ? (
                  <div htmlFor="show-openinghours" className="open-closed-label alwaysopen">
                    <Svg iconName={"clock3"} />
                    <p className="rich-text">{ReactHtmlParser(openingHoursOverwrite)}</p>
                  </div>
                ) : alwaysOpen ? (
                  <div htmlFor="show-openinghours" className="open-closed-label alwaysopen">
                    <p className="open">
                      <Svg iconName={"clock3"} />
                      <span>{merchantPageLabels.alwaysOpenText}</span>
                    </p>
                  </div>
                ) : storeIsRebuilding ? (
                  <div htmlFor="show-openinghours" className="open-closed-label closed">
                    <p className="closed">
                      <Svg iconName={"clock3"} />
                      <span>{merchantPageLabels.closedLabel}</span>
                    </p>
                  </div>
                ) : (
                  <Fragment>
                    <label htmlFor="show-openinghours" className="open-closed-label foldarrow">
                      <div className={`${merchantIsOpen ? "open" : "closed"}`}>
                        <a
                          role="button"
                          className="open-close-helper"
                          onClick={(e) => handleOpeningHours(e)}
                          onKeyDown={(e) => handleOpeningHours(e)}
                          tabIndex={0}
                        >
                          <Svg iconName={"clock3"} />
                        </a>
                        {merchantIsOpen ? (
                          <p>
                            <span>{merchantPageLabels.openNowLabel}</span> - {merchantPageLabels.closingLabel} {closingHoursForTheDay}
                          </p>
                        ) : (
                          <p>
                            <span>{merchantPageLabels.closedLabel}</span> - {merchantPageLabels.openingLabel} {openingHoursForTheDay}
                          </p>
                        )}
                      </div>
                      <div className={`days${openingHoursOpen ? "" : "  inactive"}`}>
                        <a
                          role="button"
                          className="open-close-helper"
                          onClick={(e) => handleOpeningHours(e)}
                          onKeyDown={(e) => handleOpeningHours(e)}
                          tabIndex={0}
                        >
                          <Svg iconName={"clock3"} />
                        </a>
                        {renderOpeningHours()}
                      </div>
                    </label>
                  </Fragment>
                )}
              </div>
            </div>
          </div>
          <div className="merchant-page-about-container">
            {descriptionHeader ? <h3>{descriptionHeader}</h3> : undefined}
            <div className={isReadingMore ? "readmoretext readless" : "readmoretext readmore"} onClick={() => setIsReadingMore(!isReadingMore)}>
              <div className="mechant-page-description">{ReactHtmlParser(richText)}</div>
              <button className="read-toggle" aria-expanded={isReadingMore}>
                <div className="foldicon"></div>
                <div className="text">{isReadingMore ? merchantPageLabels.readLessLabel : merchantPageLabels.readMoreLabel}</div>
              </button>
            </div>
          </div>
          <div className="bothclear"></div>
        </div>
      </div>
      {/** Same MerchantGroup Stores */}
      {otherStores && otherStores.otherStoresList && otherStores.otherStoresList.length > 0 ? (
        <MerchantPageOtherStores headline={otherStores.otherStoresLabel} merchants={otherStores.otherStoresList} cssClass={""} />
      ) : undefined}
      {/** Similar Stores */}
      {similarStores && similarStores.otherStoresList && similarStores.otherStoresList.length > 0 ? (
        <MerchantPageOtherStores
          headline={similarStores.otherStoresLabel}
          merchants={similarStores.otherStoresList}
          cssClass={"theme-main-platinum-faded"}
        />
      ) : undefined}
    </div>
  )
}

export default MerchantPage
